import { useState, useCallback, useRef } from 'react'
import type { Toast, ToastType, UseToastReturn } from './useToast.types'

export function useToast(): UseToastReturn {
  const [toasts, setToasts] = useState<Toast[]>([])
  const timersRef = useRef<Map<string, NodeJS.Timeout>>(new Map())

  const hideToast = useCallback((id: string) => {
    // Clear any existing timer for this toast
    const timer = timersRef.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timersRef.current.delete(id)
    }
    setToasts(prev => prev.filter(toast => toast.id !== id))
  }, [])

  const showToast = useCallback((
    type: ToastType,
    title: string,
    message?: string,
    duration = 5000
  ) => {
    const id = Date.now().toString()
    const timestamp = Date.now()
    const toast: Toast = { id, type, title, message, duration, timestamp }

    setToasts(prev => [...prev, toast])

    if (duration > 0) {
      const timer = setTimeout(() => {
        hideToast(id)
        timersRef.current.delete(id)
      }, duration)
      timersRef.current.set(id, timer)
    }
  }, [hideToast])

  const clearToasts = useCallback(() => {
    // Clear all timers
    timersRef.current.forEach((timer) => { clearTimeout(timer) })
    timersRef.current.clear()
    setToasts([])
  }, [])

  return {
    toasts,
    showToast,
    hideToast,
    clearToasts,
  }
}

